// 2026-06-25 v2: MapPicker — Leaflet + Nominatim with autocomplete + reverse geocoding.
// Click on the map / drag the pin -> reverse-geocode -> reports back lat, lng, AND address parts (city/state/country).
// Type in the search box -> Google-Maps-style dropdown of suggestions -> pick -> drops a pin.
import React, { useEffect, useRef, useState } from 'react';

const DEFAULT_CENTER = [12.9716, 77.5946];
const DEFAULT_ZOOM = 5;

export default function MapPicker({ lat, lng, onChange, onAddress, height = 300, searchHint }) {
  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const debounceRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchBusy, setSearchBusy] = useState(false);
  const [searchErr, setSearchErr] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => {
    if (!mapEl.current || mapRef.current) return;
    if (!window.L) { console.warn('Leaflet not loaded'); return; }
    const L = window.L;
    const center = (lat && lng) ? [Number(lat), Number(lng)] : DEFAULT_CENTER;
    const zoom = (lat && lng) ? 15 : DEFAULT_ZOOM;
    const map = L.map(mapEl.current, { center, zoom, scrollWheelZoom: true, doubleClickZoom: false });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { attribution: '© OpenStreetMap', maxZoom: 19 }).addTo(map);
    if (lat && lng) {
      markerRef.current = L.marker([Number(lat), Number(lng)], { draggable: true }).addTo(map);
      markerRef.current.on('dragend', (e) => handleNewPoint(e.target.getLatLng().lat, e.target.getLatLng().lng, true));
    }
    map.on('click', (e) => {
      placeMarker(e.latlng.lat, e.latlng.lng);
      handleNewPoint(e.latlng.lat, e.latlng.lng, true);
    });
    mapRef.current = map;
    return () => { if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!mapRef.current || lat == null || lng == null || lat === '' || lng === '') return;
    placeMarker(Number(lat), Number(lng), { recenter: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lat, lng]);

  function placeMarker(latVal, lngVal, opts = {}) {
    const L = window.L; const map = mapRef.current;
    if (!map || !L) return;
    if (markerRef.current) markerRef.current.setLatLng([latVal, lngVal]);
    else {
      markerRef.current = L.marker([latVal, lngVal], { draggable: true }).addTo(map);
      markerRef.current.on('dragend', (e) => handleNewPoint(e.target.getLatLng().lat, e.target.getLatLng().lng, true));
    }
    if (opts.recenter !== false) map.setView([latVal, lngVal], Math.max(map.getZoom(), 14));
  }

  // Common path: when a new point is set (click, drag, search, etc.) — emit lat/lng and reverse-geocode
  function handleNewPoint(la, lo, doReverse) {
    onChange?.(Number(la.toFixed(6)), Number(lo.toFixed(6)));
    if (doReverse && onAddress) reverseGeocode(la, lo);
  }

  async function reverseGeocode(la, lo) {
    try {
      const url = 'https://nominatim.openstreetmap.org/reverse?format=json&addressdetails=1&lat=' + la + '&lon=' + lo;
      const res = await fetch(url, { headers: { 'Accept': 'application/json' } });
      const j = await res.json();
      if (j && j.address) {
        const a = j.address;
        const city = a.city || a.town || a.village || a.suburb || a.hamlet || a.county || '';
        const state = a.state || a.region || '';
        const country = a.country || '';
        const postcode = a.postcode || '';
        const display = j.display_name || '';
        onAddress?.({ city, state, country, postcode, display, raw: a });
      }
    } catch (e) {
      // silent — address autofill is best-effort
    }
  }

  // Autocomplete: debounce input, call /search with multiple results, render dropdown
  function onSearchInput(v) {
    setSearchTerm(v);
    setSearchErr(null);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!v.trim() || v.trim().length < 2) { setSuggestions([]); setShowDropdown(false); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        const url = 'https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=6&q=' + encodeURIComponent(v.trim());
        const res = await fetch(url, { headers: { 'Accept': 'application/json' } });
        const arr = await res.json();
        setSuggestions(Array.isArray(arr) ? arr : []);
        setShowDropdown(true);
      } catch (e) {
        setSuggestions([]); setShowDropdown(false);
      }
    }, 320);
  }

  function pickSuggestion(s) {
    const la = Number(s.lat), lo = Number(s.lon);
    placeMarker(la, lo, { recenter: true });
    handleNewPoint(la, lo, false);  // we already have address from the suggestion
    if (onAddress && s.address) {
      const a = s.address;
      onAddress({
        city: a.city || a.town || a.village || a.suburb || a.hamlet || a.county || '',
        state: a.state || a.region || '',
        country: a.country || '',
        postcode: a.postcode || '',
        display: s.display_name || '',
        raw: a,
      });
    }
    setSearchTerm(s.display_name || '');
    setSuggestions([]);
    setShowDropdown(false);
  }

  function useMyLocation() {
    if (!navigator.geolocation) { setSearchErr('Geolocation not supported.'); return; }
    setSearchBusy(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const la = pos.coords.latitude, lo = pos.coords.longitude;
        placeMarker(la, lo, { recenter: true });
        handleNewPoint(la, lo, true);
        setSearchBusy(false);
      },
      (err) => { setSearchErr(err?.message || 'Could not get location'); setSearchBusy(false); },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  }

  return (
    <div style={{ width: '100%' }}>
      <div style={{ display: 'flex', gap: 6, marginBottom: 6, position: 'relative' }}>
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => onSearchInput(e.target.value)}
          onFocus={() => { if (suggestions.length > 0) setShowDropdown(true); }}
          onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
          placeholder={searchHint || 'Search city, street, or place name'}
          style={{ flex: 1, padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13 }}
        />
        <button type="button" onClick={useMyLocation} disabled={searchBusy} title="Use my current location"
                style={{ padding: '8px 12px', background: '#fff', color: '#374151', border: '1px solid #d1d5db',
                          borderRadius: 4, fontSize: 12, fontWeight: 700, cursor: searchBusy ? 'wait' : 'pointer' }}>
          📍 Use my location
        </button>
        {showDropdown && suggestions.length > 0 && (
          <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
                        background: '#fff', border: '1px solid #d1d5db', borderRadius: 6,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.12)', maxHeight: 260, overflowY: 'auto', zIndex: 1000 }}>
            {suggestions.map((s, i) => (
              <div key={s.place_id || i}
                   onMouseDown={() => pickSuggestion(s)}
                   style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: i === suggestions.length - 1 ? 'none' : '1px solid #f3f4f6',
                            fontSize: 12, color: '#111827' }}
                   onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                   onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}>
                <div style={{ fontWeight: 700 }}>📍 {s.name || (s.address?.city || s.address?.town || s.display_name?.split(',')[0])}</div>
                <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>{s.display_name}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {searchErr && (
        <div style={{ background: '#fee2e2', color: '#7a0000', padding: '6px 10px', borderRadius: 4, fontSize: 11, marginBottom: 6 }}>
          ⚠ {searchErr}
        </div>
      )}

      <div ref={mapEl} style={{ width: '100%', height, borderRadius: 6, border: '1px solid #d1d5db', overflow: 'hidden' }} />

      <div style={{ display: 'flex', gap: 14, marginTop: 6, fontSize: 11, color: '#6b7280' }}>
        <span><strong>Lat:</strong> {lat ? Number(lat).toFixed(6) : '—'}</span>
        <span><strong>Lng:</strong> {lng ? Number(lng).toFixed(6) : '—'}</span>
        {(lat && lng) && (
          <span style={{ marginLeft: 'auto' }}>
            <a href={`https://www.openstreetmap.org/?mlat=${lat}&mlon=${lng}&zoom=16`} target="_blank" rel="noreferrer"
               style={{ color: '#f97316', textDecoration: 'none', fontWeight: 700 }}>
              Open in OSM →
            </a>
          </span>
        )}
        <span style={{ marginLeft: (lat && lng) ? 0 : 'auto', color: '#9ca3af' }}>
          Click the map or drag the pin · Address auto-fills
        </span>
      </div>
    </div>
  );
}
